/**
 * Focus Management Composable
 *
 * Provides utilities for managing focus throughout the application,
 * including focus history, focus restoration, and programmatic focus control.
 */

import { ref, onUnmounted } from 'vue';

export interface FocusManagementOptions {
  /**
   * Whether to automatically save focus when certain elements are focused
   */
  autoSaveFocus?: boolean;

  /**
   * Maximum number of focus entries to keep in history
   */
  historyLimit?: number;

  /**
   * Whether to restore focus when the composable is unmounted
   */
  restoreOnUnmount?: boolean;
}

export type FocusableElement = HTMLElement & {
  focus: () => void;
  blur: () => void;
};

export const useFocusManagement = (options: FocusManagementOptions = {}) => {
  const { autoSaveFocus = false, historyLimit = 10, restoreOnUnmount = false } = options;

  // Focus history stack
  const focusHistory = ref<FocusableElement[]>([]);

  // Currently focused element reference
  const currentFocus = ref<FocusableElement | null>(null);

  /**
   * Save the current active element to focus history
   */
  const saveFocus = (element?: FocusableElement) => {
    const elementToSave = element || (document.activeElement as FocusableElement);

    if (elementToSave && typeof elementToSave.focus === 'function') {
      // Avoid duplicate entries
      if (focusHistory.value[focusHistory.value.length - 1] !== elementToSave) {
        focusHistory.value.push(elementToSave);

        // Maintain history limit
        if (focusHistory.value.length > historyLimit) {
          focusHistory.value.shift();
        }
      }

      currentFocus.value = elementToSave;
    }
  };

  /**
   * Restore focus to the most recently saved element
   */
  const restoreFocus = (fallbackSelector?: string): boolean => {
    const lastFocused = focusHistory.value.pop();

    if (lastFocused && document.contains(lastFocused)) {
      try {
        lastFocused.focus();
        currentFocus.value = lastFocused;
        return true;
      } catch (error) {
        console.warn('Failed to restore focus to element:', error);
      }
    }

    // Fallback to selector if provided
    if (fallbackSelector) {
      const fallbackElement = document.querySelector(fallbackSelector) as FocusableElement;
      if (fallbackElement && typeof fallbackElement.focus === 'function') {
        try {
          fallbackElement.focus();
          currentFocus.value = fallbackElement;
          return true;
        } catch (error) {
          console.warn('Failed to focus fallback element:', error);
        }
      }
    }

    return false;
  };

  /**
   * Set focus to a specific element by reference or selector
   */
  const setFocus = (target: FocusableElement | string, savePrevious = true): boolean => {
    if (savePrevious) {
      saveFocus();
    }

    let element: FocusableElement | null = null;

    if (typeof target === 'string') {
      element = document.querySelector(target) as FocusableElement;
    } else {
      element = target;
    }

    if (element && typeof element.focus === 'function') {
      try {
        element.focus();
        currentFocus.value = element;
        return true;
      } catch (error) {
        console.warn('Failed to set focus to element:', error);
      }
    }

    return false;
  };

  /**
   * Clear focus history
   */
  const clearHistory = () => {
    focusHistory.value = [];
    currentFocus.value = null;
  };

  /**
   * Get focusable elements within a container
   */
  const getFocusableElements = (container: HTMLElement): FocusableElement[] => {
    const focusableSelectors = [
      'button:not([disabled])',
      '[href]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
      '[contenteditable="true"]:not([disabled])',
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as FocusableElement[];
  };

  /**
   * Move focus to the next focusable element in the container
   */
  const focusNext = (container?: HTMLElement): boolean => {
    const containerEl = container || document.body;
    const focusableElements = getFocusableElements(containerEl);
    const currentIndex = focusableElements.indexOf(document.activeElement as FocusableElement);

    if (currentIndex < focusableElements.length - 1) {
      const nextElement = focusableElements[currentIndex + 1];
      return setFocus(nextElement, false);
    }

    return false;
  };

  /**
   * Move focus to the previous focusable element in the container
   */
  const focusPrevious = (container?: HTMLElement): boolean => {
    const containerEl = container || document.body;
    const focusableElements = getFocusableElements(containerEl);
    const currentIndex = focusableElements.indexOf(document.activeElement as FocusableElement);

    if (currentIndex > 0) {
      const previousElement = focusableElements[currentIndex - 1];
      return setFocus(previousElement, false);
    }

    return false;
  };

  /**
   * Move focus to the first focusable element in the container
   */
  const focusFirst = (container?: HTMLElement): boolean => {
    const containerEl = container || document.body;
    const focusableElements = getFocusableElements(containerEl);

    if (focusableElements.length > 0) {
      return setFocus(focusableElements[0], false);
    }

    return false;
  };

  /**
   * Move focus to the last focusable element in the container
   */
  const focusLast = (container?: HTMLElement): boolean => {
    const containerEl = container || document.body;
    const focusableElements = getFocusableElements(containerEl);

    if (focusableElements.length > 0) {
      return setFocus(focusableElements[focusableElements.length - 1], false);
    }

    return false;
  };

  /**
   * Check if an element is focusable
   */
  const isFocusable = (element: Element): boolean => {
    if (!element || element.hasAttribute('disabled')) {
      return false;
    }

    const focusableSelectors = [
      'button',
      'a[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]',
      '[contenteditable="true"]',
    ];

    return focusableSelectors.some(selector => {
      if (selector === '[tabindex]') {
        const tabindex = element.getAttribute('tabindex');
        return tabindex !== null && tabindex !== '-1';
      }
      return element.matches(selector);
    });
  };

  /**
   * Setup auto-save focus behavior
   */
  if (autoSaveFocus) {
    const handleFocusIn = (event: FocusEvent) => {
      if (event.target && isFocusable(event.target as Element)) {
        saveFocus(event.target as FocusableElement);
      }
    };

    document.addEventListener('focusin', handleFocusIn);

    onUnmounted(() => {
      document.removeEventListener('focusin', handleFocusIn);
    });
  }

  /**
   * Restore focus on unmount if enabled
   */
  if (restoreOnUnmount) {
    onUnmounted(() => {
      restoreFocus();
    });
  }

  return {
    // State
    focusHistory: focusHistory.value,
    currentFocus,

    // Core methods
    saveFocus,
    restoreFocus,
    setFocus,
    clearHistory,

    // Navigation methods
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,

    // Utility methods
    getFocusableElements,
    isFocusable,
  };
};

export default useFocusManagement;
